import './MapOverview.css';
import React, { useEffect, useRef, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L, { Marker as LeafletMarker } from 'leaflet';
import blue_marker from '../Images/map/blue_marker.png';
import red_marker from '../Images/map/red_marker.png';
import grey_marker from '../Images/map/grey_marker.png';
import { createCustomIcon } from './CustomIcon';
import {Button} from "react-bootstrap";
import MapPopup from "./MapPopup";
import {BuildingSummary} from "../types/protocols";

interface CoordinateItem {
    coords: [number, number];
    building: BuildingSummary;
    isFiltered?: boolean;
}

interface MapComponentProps {
    allCoordinates: CoordinateItem[];
    focusCoordinates: { coords: [number, number] } [];
    highlightedId?: string;
    popupInfo?: { id: string; coordinates: [number, number] } | null;
    onMarkerClick?: (id: string) => void;
    shouldResizeBounds: boolean;
    buildingswithCovers?: BuildingSummary[];
}

const FitBoundsHandler: React.FC<{ bounds: L.LatLngBoundsExpression, shouldResizeBounds: Boolean }> = ({ bounds, shouldResizeBounds }) => {
    const map = useMap();
    const previousBoundsRef = useRef<string>();

    useEffect(() => {
        if (!shouldResizeBounds) {
            return;
        }

        const boundsString = JSON.stringify(bounds);
        if (Array.isArray(bounds) && bounds.length > 0 && boundsString !== previousBoundsRef.current) {
            map.fitBounds(bounds, { padding: [20, 20] });
            previousBoundsRef.current = boundsString;
        }
    }, [bounds, map, shouldResizeBounds]);

    return null;
};

const MapComponent: React.FC<MapComponentProps> = ({
                                                       allCoordinates,
                                                       focusCoordinates,
                                                       highlightedId,
                                                       popupInfo,
                                                       onMarkerClick,
                                                       shouldResizeBounds,
                                                       buildingswithCovers,
                                                   }) => {
    const [isSatellite, setIsSatellite] = useState(false);
    const markerRefs = useRef<{ [key: string]: LeafletMarker | null }>({});

    const defaultIcon = createCustomIcon(blue_marker);
    const highlightedIcon = createCustomIcon(red_marker);
    const greyIcon = createCustomIcon(grey_marker);

    const focusBounds = focusCoordinates.map((loc) => loc.coords);

    useEffect(() => {
        if (popupInfo && markerRefs.current[popupInfo.id]) {
            markerRefs.current[popupInfo.id]?.openPopup();
        }
    }, [popupInfo]);

    const toggleTileLayer = () => {
        setIsSatellite((prev) => !prev);
    };

    return (
        <div style={{ position: 'relative', height: '100%', width: '100%' }}>
            <MapContainer center={[59.3293, 18.0686]} zoom={13} style={{ height: '100%', width: '100%' }}>
                {!isSatellite ? (
                    <TileLayer
                        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    />
                ) : (
                    <TileLayer
                        url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                        attribution='&copy; <a href="https://www.esri.com/">Esri</a> contributors'
                        maxZoom={19}
                    />
                )}

                {focusBounds.length > 0 && <FitBoundsHandler bounds={focusBounds} shouldResizeBounds={shouldResizeBounds} />}

                {allCoordinates.map((item) => {
                    const filteredBuildings = buildingswithCovers?.filter(
                        (b) => b.key === item.building.key
                    );

                    let iconToUse = item.building.key === highlightedId ? highlightedIcon
                        : item.isFiltered ? defaultIcon
                            : greyIcon;

                    return (
                        <Marker
                            key={item.building.key}
                            position={item.coords}
                            icon={iconToUse}
                            ref={(el) => (markerRefs.current[item.building.key] = el)}
                            eventHandlers={{
                                click: () => onMarkerClick?.(item.building.key),
                            }}
                            zIndexOffset={item.building.key === highlightedId ? 100 : 0}
                        >
                            <MapPopup
                                building={item.building}
                            />
                        </Marker>
                    );
                })}
            </MapContainer>

            <Button
                style={{
                    position: 'absolute',
                    top: '10px',
                    right: '10px',
                    zIndex: 1000,
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.3)',
                    transition: 'background-color 0.3s, color 0.3s',
                }}
                variant="light"
                onClick={toggleTileLayer}
            >
                <img
                    style={{
                        width: '30px',
                        padding: '2px',
                        color: '#ffffff'}}
                    alt={"Map layers"}
                    src={'/Images/layers.png'}
                />
            </Button>
        </div>
    );
};

export default MapComponent;